import classNames from "classnames";
import {
  type Control,
  Controller,
  type FieldError,
  type Path
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CustomFormFieldWrapper } from "../../../BuildingBlocks/Forms/MantineFormFields/CustomFormFieldWrapper/CustomFormFieldWrapper";
import type { Contract, Price } from "../../DeliveryOverview.types";
import type { DeliveryFormFields } from "../DeliveryModal/DeliveryForm/DeliveryForm.types";
import type { ChangeablePrice } from "./ChangeablePriceField/ChangeablePriceField";
import { ChangeablePriceField } from "./ChangeablePriceField/ChangeablePriceField";

interface ChangeablePricesFieldProps<T extends DeliveryFormFields | Contract> {
  control: Control<T>;
  error?: FieldError;
  name: Path<T>;
  label: string;
  unit: string;
  className?: string;
  allowInput?: boolean;
  collapsible?: boolean;
  previewValue?: number;
  helpText?: string;
}

function ChangeablePricesField<T extends DeliveryFormFields | Contract>({
  control,
  error,
  name,
  label,
  unit,
  className,
  allowInput = true,
  collapsible = true,
  previewValue,
  helpText
}: ChangeablePricesFieldProps<T>) {
  const { t } = useTranslation();

  function validatePrices(value?: Array<ChangeablePrice>) {
    if (previewValue) {
      return true;
    }
    if (value && value.length > 0) {
      for (const price of value) {
        if (price.value === null || typeof price.value === "undefined") {
          return t("errors.ContractForm.PriceValueMissing");
        } else if (price.value < 0) {
          return t("errors.ContractForm.NegativePrice");
        }
      }

      return true;
    }

    return t("errors.Required");
  }

  return (
    <CustomFormFieldWrapper
      className={classNames("ChangeablePriceField", className)}
      error={error}
      helpText={helpText}
      label={label}
      name={name}
    >
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <ChangeablePriceField
            {...field}
            allowInput={allowInput}
            collapsible={collapsible}
            fieldName={field.name}
            unit={unit}
            value={
              previewValue
                ? [{ first_date: "", last_date: "", value: previewValue }]
                : (field.value as Price[]) || []
            }
            onInput={(_, value) => field.onChange(value)}
          />
        )}
        rules={{
          validate: (value) => validatePrices(value as ChangeablePrice[])
        }}
      />
    </CustomFormFieldWrapper>
  );
}

export { ChangeablePricesField };
